import React, { useEffect, useMemo, useState } from 'react';
import { X, Calendar, ChevronLeft, ChevronRight, User, Clock, FileText, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

import studyBuilderService from '../../services/studyBuilderService';
import Tooltip from '../Shared/Tooltip';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Stage colour palette: soft tints that keep stages apart in light and dark
// mode. The first stage uses the brand tint. Tailwind needs full class
// literals to retain them in the build, so each variant is spelled out.
const STAGE_PALETTE = [
  { dot: 'bg-primary-500', badge: 'bg-primary-50 text-primary-800 ring-1 ring-inset ring-primary-600/20 dark:bg-primary-400/15 dark:text-primary-200 dark:ring-primary-400/30', cell: 'bg-primary-50/70 dark:bg-primary-400/[0.07]', ring: 'ring-primary-400' },
  { dot: 'bg-sky-500',     badge: 'bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-600/20 dark:bg-sky-400/15 dark:text-sky-200 dark:ring-sky-400/30',                 cell: 'bg-sky-50/70 dark:bg-sky-400/[0.07]',         ring: 'ring-sky-400' },
  { dot: 'bg-amber-500',   badge: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-600/25 dark:bg-amber-400/15 dark:text-amber-200 dark:ring-amber-400/30',     cell: 'bg-amber-50/70 dark:bg-amber-400/[0.07]',     ring: 'ring-amber-400' },
  { dot: 'bg-violet-500',  badge: 'bg-violet-50 text-violet-800 ring-1 ring-inset ring-violet-600/20 dark:bg-violet-400/15 dark:text-violet-200 dark:ring-violet-400/30', cell: 'bg-violet-50/70 dark:bg-violet-400/[0.07]',   ring: 'ring-violet-400' },
  { dot: 'bg-rose-500',    badge: 'bg-rose-50 text-rose-800 ring-1 ring-inset ring-rose-600/20 dark:bg-rose-400/15 dark:text-rose-200 dark:ring-rose-400/30',           cell: 'bg-rose-50/70 dark:bg-rose-400/[0.07]',       ring: 'ring-rose-400' },
  { dot: 'bg-gray-400',    badge: 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-500/20 dark:bg-white/[0.08] dark:text-gray-200 dark:ring-white/15',           cell: 'bg-gray-100/70 dark:bg-white/[0.04]',         ring: 'ring-gray-400' },
];

// Neutral marker for the study start and last survey day in a calendar cell.
const MARKER_TAG = 'inline-block rounded px-1 py-0.5 text-[10px] font-medium leading-none bg-white text-gray-700 ring-1 ring-inset ring-gray-300 dark:bg-gray-900 dark:text-gray-200 dark:ring-white/20';

const stageColor = (idx) => STAGE_PALETTE[((idx ?? 0) % STAGE_PALETTE.length + STAGE_PALETTE.length) % STAGE_PALETTE.length];

const isoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const parseISODate = (s) => {
  if (!s) return null;
  const [y, m, day] = s.split('-').map(Number);
  return new Date(y, m - 1, day);
};

const addDays = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

const startOfWeek = (d) => addDays(d, -d.getDay());

const sameDate = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const formatLongDate = (d) =>
  d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

const formatShortDate = (d) =>
  d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const formatMonthDay = (d) =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const daysBetween = (a, b) =>
  Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));

const describeOffset = (enrollment, studyStart) => {
  if (!enrollment || !studyStart) return '';
  const diff = daysBetween(enrollment, studyStart);
  if (diff === 0) return 'same day as enrollment';
  if (diff === 1) return '1 day after enrollment';
  if (diff < 0) return `${Math.abs(diff)} day${Math.abs(diff) === 1 ? '' : 's'} before enrollment`;
  return `${diff} days after enrollment`;
};

const formatTargetTime = (t) => {
  // Accept "HH:MM" or "HH:MM:SS"; render as "9:00 AM"
  if (!t) return '';
  const [h, m] = t.split(':').map((v) => parseInt(v, 10));
  if (Number.isNaN(h)) return t;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
};

const buildScheduleConfigForPreview = (schedule, outerStudyStartDay) => {
  const config = {
    ...schedule,
    studyStartDay: outerStudyStartDay !== '' && outerStudyStartDay !== null && outerStudyStartDay !== undefined
      ? outerStudyStartDay
      : schedule.studyStartDay,
  };
  return config;
};

const summarizeScheduleType = (schedule) => {
  if (!schedule?.type) return 'Schedule';
  if (schedule.type === 'Patient Prompted (Daily)') return 'Patient Prompted (Daily)';
  return schedule.type;
};

const IMPLIED_DAILY_TYPES = ['Patient Prompted (Daily)', 'Daily Assessment'];

const summarizeCadence = (schedule) => {
  if (IMPLIED_DAILY_TYPES.includes(schedule?.type)) {
    return 'Daily surveys automatically enforced';
  }
  const stage = schedule?.stages?.[0];
  if (!stage?.frequency) return '';
  const value = stage.frequency.value || '';
  const unit = stage.frequency.unit || 'days';
  return value ? `Every ${value} ${unit}` : '';
};

const SchedulePreviewModal = ({
  open,
  onClose,
  // Preferred multi-schedule API
  schedules,
  editingSchedule,
  editingScheduleIndex,
  // Legacy single-schedule API (still supported)
  schedule: legacySchedule,
  studyStartDay,
}) => {
  const today = useMemo(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), t.getDate());
  }, []);

  // Build the canonical list of schedules to switch between. When a schedule is
  // being edited, its draft form state replaces the saved copy at that index so
  // the visualizer reflects unsaved changes.
  const allSchedules = useMemo(() => {
    if (!Array.isArray(schedules) || schedules.length === 0) {
      return legacySchedule ? [legacySchedule] : (editingSchedule ? [editingSchedule] : []);
    }
    if (editingSchedule && typeof editingScheduleIndex === 'number' && editingScheduleIndex >= 0) {
      return schedules.map((s, i) => (i === editingScheduleIndex ? editingSchedule : s));
    }
    return schedules;
  }, [schedules, editingSchedule, editingScheduleIndex, legacySchedule]);

  const [activeIdx, setActiveIdx] = useState(() =>
    typeof editingScheduleIndex === 'number' && editingScheduleIndex >= 0 ? editingScheduleIndex : 0
  );
  const activeSchedule = allSchedules[activeIdx] || allSchedules[0];

  const [enrollmentDate, setEnrollmentDate] = useState(isoDate(today));
  const [view, setView] = useState('week');
  const [focusDate, setFocusDate] = useState(today);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIso, setSelectedIso] = useState(null);
  const [filterStageIdx, setFilterStageIdx] = useState(null); // null = all stages

  const browserTz = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    []
  );

  // Reset state each time the modal opens
  useEffect(() => {
    if (open) {
      const todayIso = isoDate(today);
      setEnrollmentDate(todayIso);
      setView('week');
      setFocusDate(today);
      setPreview(null);
      setSelectedIso(null);
      setFilterStageIdx(null);
      setActiveIdx(
        typeof editingScheduleIndex === 'number' && editingScheduleIndex >= 0 ? editingScheduleIndex : 0
      );
    }
  }, [open, today, editingScheduleIndex]);

  // When switching between schedules, drop any open day detail / stage filter
  useEffect(() => {
    setSelectedIso(null);
    setFilterStageIdx(null);
    setPreview(null);
  }, [activeIdx]);

  useEffect(() => {
    if (!open || !activeSchedule || !enrollmentDate) return;

    const config = buildScheduleConfigForPreview(activeSchedule, studyStartDay);
    let cancelled = false;
    setLoading(true);
    studyBuilderService
      .previewSchedule(config, enrollmentDate, browserTz)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        const enrolled = parseISODate(data.enrollment_date);
        if (enrolled) setFocusDate(enrolled);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.response?.data?.error || err?.message || 'Failed to preview schedule';
        toast.error(msg);
        setPreview(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, activeSchedule, studyStartDay, enrollmentDate, browserTz]);

  const occurrencesByDate = useMemo(() => {
    const map = new Map();
    if (preview?.occurrences) {
      for (const occ of preview.occurrences) {
        // Apply stage filter: keep only timepoints from the filtered stage
        if (filterStageIdx !== null) {
          const filteredTps = occ.timepoints.filter((tp) => tp.stage_index === filterStageIdx);
          if (filteredTps.length === 0) continue;
          map.set(occ.date, {
            ...occ,
            timepoints: filteredTps,
            stage_indices: [filterStageIdx],
          });
        } else {
          map.set(occ.date, occ);
        }
      }
    }
    return map;
  }, [preview, filterStageIdx]);

  const enrollmentDateObj = preview ? parseISODate(preview.enrollment_date) : parseISODate(enrollmentDate);
  const studyStartDateObj = preview ? parseISODate(preview.study_start_date) : null;
  const selectedOccurrence = selectedIso ? occurrencesByDate.get(selectedIso) : null;
  const selectedDateObj = selectedIso ? parseISODate(selectedIso) : null;

  const visibleDays = useMemo(() => {
    if (view === 'week') {
      const start = startOfWeek(focusDate);
      return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    }
    const monthStart = new Date(focusDate.getFullYear(), focusDate.getMonth(), 1);
    const gridStart = startOfWeek(monthStart);
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [view, focusDate]);

  const headerLabel = useMemo(() => {
    if (view === 'week') {
      const start = startOfWeek(focusDate);
      return `Week of ${MONTHS[start.getMonth()].slice(0, 3)} ${start.getDate()}, ${start.getFullYear()}`;
    }
    return `${MONTHS[focusDate.getMonth()]} ${focusDate.getFullYear()}`;
  }, [view, focusDate]);

  const stepCalendar = (direction) => {
    if (view === 'week') {
      setFocusDate((d) => addDays(d, 7 * direction));
    } else {
      setFocusDate((d) => new Date(d.getFullYear(), d.getMonth() + direction, 1));
    }
  };

  const handleCellClick = (iso, hasOccurrence) => {
    if (!hasOccurrence) return;
    setSelectedIso((curr) => (curr === iso ? null : iso));
    const target = parseISODate(iso);
    if (target) setFocusDate(target);
  };

  const surveyName = (sid) => preview?.surveys?.[sid]?.name || `Survey ${sid.slice(0, 6)}…`;
  const stageName = (idx) => preview?.stages?.find((s) => s.index === idx)?.name || `Stage ${idx + 1}`;

  if (!open) return null;

  const stages = preview?.stages || [];
  const summary = preview?.summary;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70">
      <div className={`flex max-h-[95vh] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 transition-all dark:bg-gray-800 dark:ring-white/10 ${selectedOccurrence ? 'max-w-6xl' : 'max-w-4xl'}`}>
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-100 px-6 pb-4 pt-5 dark:border-white/[0.06]">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <Calendar className="h-[18px] w-[18px] text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">Schedule Visualizer</h2>
            </div>
            <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
              {summary && summary.total_survey_instances > 0 ? (
                <>
                  <strong className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">{summary.total_survey_instances}</strong> survey{summary.total_survey_instances === 1 ? '' : 's'}
                  {' · '}
                  <strong className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">{summary.stage_count}</strong> stage{summary.stage_count === 1 ? '' : 's'}
                  {' · '}
                  <strong className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">{summary.span_days}</strong> day{summary.span_days === 1 ? '' : 's'}
                  {summary.first_survey_date && summary.last_survey_date && (
                    <>
                      {' · '}
                      {formatMonthDay(parseISODate(summary.first_survey_date))}
                      {' → '}
                      {formatMonthDay(parseISODate(summary.last_survey_date))}
                    </>
                  )}
                </>
              ) : (
                'Preview participant study timeline'
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="-mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
            aria-label="Close visualizer"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 overflow-y-auto px-6 py-5">
          {/* Schedule picker (shown only when there are multiple schedules) */}
          {allSchedules.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="shrink-0 text-[12.5px] font-medium text-gray-500 dark:text-gray-400">
                Schedule
              </span>
              <div className="flex flex-wrap gap-1.5">
                {allSchedules.map((sch, idx) => {
                  const isActive = idx === activeIdx;
                  const label = sch?.name?.trim() || summarizeScheduleType(sch) || `Schedule ${idx + 1}`;
                  const arms = Array.isArray(sch?.arms) ? sch.arms : [];
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveIdx(idx)}
                      className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                        isActive
                          ? 'border-primary-600 bg-primary-50 text-primary-800 dark:border-primary-400/60 dark:bg-primary-400/10 dark:text-primary-200'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-white/10 dark:bg-transparent dark:text-gray-300 dark:hover:border-white/20'
                      }`}
                    >
                      <span className="max-w-[160px] truncate">{label}</span>
                      {arms.length > 0 && (
                        <span className="flex items-center gap-1">
                          {arms.map((arm) => (
                            <span
                              key={arm}
                              className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none ${
                                isActive
                                  ? 'bg-primary-100/80 text-primary-800 dark:bg-primary-400/20 dark:text-primary-100'
                                  : 'bg-gray-100 text-gray-600 dark:bg-white/[0.08] dark:text-gray-300'
                              }`}
                            >
                              {arm}
                            </span>
                          ))}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top info row */}
          <div className="grid grid-cols-1 divide-y divide-gray-100 rounded-xl border border-gray-200/80 md:grid-cols-3 md:divide-x md:divide-y-0 dark:divide-white/[0.06] dark:border-white/[0.07]">
            <div className="p-3.5">
              <Tooltip
                content="Simulate a participant's enrollment date to preview how this schedule will roll out for them. Change the date to see how the study start, stages, and survey windows shift based on the current schedule configuration."
                iconClassName="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ml-1"
              >
                <label className="flex items-center gap-1.5 text-[12.5px] font-medium text-gray-500 dark:text-gray-400">
                  <User className="h-3.5 w-3.5" />
                  Enrollment Date
                </label>
              </Tooltip>
              <input
                type="date"
                value={enrollmentDate}
                onChange={(e) => setEnrollmentDate(e.target.value)}
                className="input mt-1.5 h-9"
              />
              {enrollmentDateObj && (
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  {formatShortDate(enrollmentDateObj)}
                </p>
              )}
            </div>

            <div className="p-3.5">
              <p className="text-[12.5px] font-medium text-gray-500 dark:text-gray-400">Schedule</p>
              <p className="mt-1.5 text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">
                {summarizeScheduleType(activeSchedule)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{summarizeCadence(activeSchedule)}</p>
              {Array.isArray(activeSchedule?.arms) && activeSchedule.arms.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {activeSchedule.arms.map((arm) => (
                    <span
                      key={arm}
                      className="badge badge-gray px-1.5 text-[10.5px]"
                    >
                      {arm}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {studyStartDateObj && (
              <div className="p-3.5">
                <p className="flex items-center gap-1.5 text-[12.5px] font-medium text-gray-500 dark:text-gray-400">
                  <Calendar className="h-3.5 w-3.5" />
                  Study Start (Day {preview.study_start_day})
                </p>
                <p className="mt-1.5 text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">{formatShortDate(studyStartDateObj)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{describeOffset(enrollmentDateObj, studyStartDateObj)}</p>
              </div>
            )}
          </div>

          {/* Stage overview strip */}
          {stages.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-gray-200/80 dark:border-white/[0.07]">
              <div className="flex items-center justify-between bg-gray-50/70 px-3.5 py-2 dark:bg-white/[0.02]">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
                  Stages
                  {filterStageIdx !== null && (
                    <span className="ml-2 text-[11px] font-normal text-gray-500 dark:text-gray-400">
                      (filtered to {stageName(filterStageIdx)})
                    </span>
                  )}
                </p>
                {filterStageIdx !== null && (
                  <button
                    onClick={() => setFilterStageIdx(null)}
                    className="text-[11.5px] font-medium text-primary-700 hover:underline dark:text-primary-400"
                  >
                    Show all
                  </button>
                )}
              </div>
              <div className="divide-y divide-gray-100 border-t border-gray-100 dark:divide-white/[0.06] dark:border-white/[0.06]">
                {stages.map((stage) => {
                  const color = stageColor(stage.index);
                  const isFiltered = filterStageIdx === stage.index;
                  const dimmed = filterStageIdx !== null && !isFiltered;
                  return (
                    <button
                      key={stage.index}
                      onClick={() => setFilterStageIdx(isFiltered ? null : stage.index)}
                      className={`flex w-full items-center gap-3 px-3.5 py-2 text-left text-xs transition-colors hover:bg-gray-50/80 dark:hover:bg-white/[0.03] ${
                        dimmed ? 'opacity-50' : ''
                      } ${isFiltered ? 'bg-primary-50/60 dark:bg-primary-400/[0.06]' : ''}`}
                    >
                      <span className={`h-2 w-2 shrink-0 rounded-full ${color.dot}`}></span>
                      <span className="min-w-[80px] truncate font-medium text-gray-800 dark:text-gray-100">
                        {stage.name}
                      </span>
                      <span className="whitespace-nowrap tabular-nums text-gray-500 dark:text-gray-400">
                        Day {stage.start_day}–{stage.end_day}
                      </span>
                      <span className="whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {stage.frequency_label}
                      </span>
                      <span className="ml-auto whitespace-nowrap tabular-nums text-gray-500 dark:text-gray-400">
                        {stage.occurrence_count} occurrence{stage.occurrence_count === 1 ? '' : 's'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Calendar + detail (side-by-side when a day is selected) */}
          <div className={`grid gap-4 ${selectedOccurrence ? 'grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px]' : 'grid-cols-1'}`}>
            <div className="min-w-0 space-y-2.5">
          {/* Calendar header */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1">
              <button
                onClick={() => stepCalendar(-1)}
                className="btn btn-ghost btn-sm btn-icon"
                aria-label="Previous"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="min-w-[10.5rem] text-center text-sm font-semibold text-gray-900 dark:text-gray-50">{headerLabel}</span>
              <button
                onClick={() => stepCalendar(1)}
                className="btn btn-ghost btn-sm btn-icon"
                aria-label="Next"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <div className="segmented">
              <button
                onClick={() => setView('week')}
                className={`segmented-item ${view === 'week' ? 'segmented-item-active' : ''}`}
              >
                Week
              </button>
              <button
                onClick={() => setView('month')}
                className={`segmented-item ${view === 'month' ? 'segmented-item-active' : ''}`}
              >
                Month
              </button>
            </div>
          </div>

          {/* Calendar grid */}
          <div className="overflow-hidden rounded-xl border border-gray-200/80 dark:border-white/[0.07]">
            <div className="grid grid-cols-7 bg-gray-50/70 text-[11.5px] font-medium text-gray-500 dark:bg-white/[0.02] dark:text-gray-400">
              {WEEKDAYS.map((wd) => (
                <div key={wd} className="px-2 py-1.5 text-center">{wd}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {visibleDays.map((d) => {
                const iso = isoDate(d);
                const occ = occurrencesByDate.get(iso);
                const isEnrollment = enrollmentDateObj && sameDate(d, enrollmentDateObj);
                const isStudyStart = studyStartDateObj && sameDate(d, studyStartDateObj);
                const isLastSurvey = summary?.last_survey_date && iso === summary.last_survey_date;
                const inFocusMonth = view === 'month' ? d.getMonth() === focusDate.getMonth() : true;
                const isSelected = selectedIso === iso;
                const stageIndices = occ?.stage_indices || [];
                const primaryColor = stageIndices.length > 0 ? stageColor(stageIndices[0]) : null;

                const cellHeight = view === 'month' ? 'min-h-[60px]' : 'min-h-[72px]';
                const stageBg = primaryColor ? primaryColor.cell : '';
                const selectedRing = isSelected ? 'ring-2 ring-inset ring-primary-600 dark:ring-primary-400' : '';
                const enrollmentRing = isEnrollment && !isSelected ? 'outline-dashed outline-1 -outline-offset-4 outline-gray-500 dark:outline-gray-400' : '';

                return (
                  <div
                    key={iso}
                    onClick={() => handleCellClick(iso, !!occ)}
                    className={`${cellHeight} flex flex-col gap-0.5 border-l border-t border-gray-100 p-1.5 text-xs [&:nth-child(7n+1)]:border-l-0 dark:border-white/[0.06] ${
                      inFocusMonth ? '' : 'bg-gray-50/60 text-gray-400 dark:bg-white/[0.015] dark:text-gray-600'
                    } ${stageBg} ${enrollmentRing} ${selectedRing} ${occ ? 'cursor-pointer transition-[filter] hover:brightness-[0.97] dark:hover:brightness-125' : ''}`}
                  >
                    <div className="flex items-center justify-between leading-none">
                      <span className={`font-medium tabular-nums ${inFocusMonth ? 'text-gray-800 dark:text-gray-200' : ''}`}>
                        {d.getDate()}
                      </span>
                      {occ && (
                        <span className="text-[9.5px] tabular-nums text-gray-500 dark:text-gray-400">
                          D{occ.day_number}
                        </span>
                      )}
                    </div>
                    {isStudyStart && (
                      <span className={`${MARKER_TAG} self-start`}>
                        Start
                      </span>
                    )}
                    {isLastSurvey && (
                      <span className={`${MARKER_TAG} self-start`}>
                        End
                      </span>
                    )}
                    {occ && stageIndices.slice(0, 2).map((sIdx) => {
                      const c = stageColor(sIdx);
                      const label = stageName(sIdx);
                      return (
                        <span
                          key={sIdx}
                          className={`inline-block truncate rounded px-1 py-0.5 text-[10px] font-medium leading-none ${c.badge}`}
                          title={label}
                        >
                          {label}
                        </span>
                      );
                    })}
                    {occ && stageIndices.length > 2 && (
                      <span className="text-[9.5px] leading-none text-gray-500 dark:text-gray-400">
                        +{stageIndices.length - 2} more
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
            </div>

            {/* Day detail side panel */}
            {selectedOccurrence && selectedDateObj && (
              <div className="rounded-xl border border-gray-200/80 p-4 lg:max-h-[460px] lg:overflow-y-auto dark:border-white/[0.07]">
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-50">
                      {formatShortDate(selectedDateObj)}
                    </p>
                    <p className="mt-0.5 text-xs tabular-nums text-gray-500 dark:text-gray-400">
                      Day {selectedOccurrence.day_number} · Visit {selectedOccurrence.visit_number}
                    </p>
                    {selectedOccurrence.stage_indices?.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-x-2.5 gap-y-1">
                        {selectedOccurrence.stage_indices.map((idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300"
                          >
                            <span className={`h-1.5 w-1.5 rounded-full ${stageColor(idx).dot}`}></span>
                            {stageName(idx)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedIso(null)}
                    className="-mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
                    aria-label="Close detail"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="divide-y divide-gray-100 border-t border-gray-100 dark:divide-white/[0.06] dark:border-white/[0.06]">
                  {selectedOccurrence.timepoints.map((tp, tpIdx) => {
                    const c = stageColor(tp.stage_index);
                    return (
                      <div
                        key={`${tp.id}-${tpIdx}`}
                        className="py-2.5"
                      >
                        <div className="mb-1 flex min-w-0 items-center gap-2">
                          <span className={`h-2 w-2 shrink-0 rounded-full ${c.dot}`}></span>
                          <span className="flex-1 truncate text-xs font-medium text-gray-900 dark:text-gray-50">
                            {tp.name}
                          </span>
                        </div>
                        <div className="mb-1 flex items-center gap-1 text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
                          <Clock className="h-3 w-3" />
                          {formatTargetTime(tp.target_time)}
                          <span className="text-gray-400 dark:text-gray-500">
                            · {tp.window_minutes}m window
                          </span>
                        </div>
                        <ul className="space-y-0.5 pl-1">
                          {tp.survey_ids.length === 0 && (
                            <li className="text-[11px] text-gray-500 dark:text-gray-400">No surveys assigned</li>
                          )}
                          {tp.survey_ids.map((sid) => (
                            <li
                              key={sid}
                              className="flex items-center gap-1.5 text-[11.5px] text-gray-600 dark:text-gray-300"
                            >
                              <FileText className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
                              <span className="truncate">{surveyName(sid)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px] text-gray-600 dark:text-gray-300">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-sm border border-dashed border-gray-500 dark:border-gray-400"></span>
              Enrollment
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className={MARKER_TAG}>Start</span>
              Study Start
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className={MARKER_TAG}>End</span>
              Last Survey
            </span>
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <span className="text-gray-500 dark:text-gray-400">
              Click any survey day for details · Click a stage to filter
            </span>
            {loading && (
              <span className="ml-auto inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary-600 dark:text-primary-400" />
                Computing…
              </span>
            )}
            {!loading && preview && preview.occurrences.length === 0 && (
              <span className="ml-auto text-gray-500 dark:text-gray-400">
                No surveys scheduled. Check stage day bounds and frequency.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchedulePreviewModal;
